import { Tenancy } from '../hooks/useTenancy';
import { IECommerceSiteConfig, IProduct, IShippingOption, ShippingCode } from './Product';
import { Cart, Series3UpgradeCart, StoreCart, SubscriptionCart } from './carts';
import { GiftRecipientInput } from './gql-op-types';

export * from './Product';
export * from './carts';
export * from './gql';

//
// General Types
//
export type Money = string;

export type ProductID = string;
export type CartItemID = string;

export type Result<S, F> = { kind: 'success'; value: S } | { kind: 'failure'; err: F };

/**
 * The config that is loaded from the server.
 */
export interface RemoteConfig {
  products: IProduct[];
  config: IECommerceSiteConfig;
  shippingOptions: IShippingOption[];
}

/**
 * The config that is stored in Redux.
 */
export interface ECommerceConfig {
  upgradeFlow: boolean;
  loaded: boolean;
  products: IProduct[];
  shippingOptions: IShippingOption[];
  siteConfig: IECommerceSiteConfig;
}

export interface MakerBandDetails {
  bandMakerName: string;
  bandPriceInCents?: number;
}

//
// Session Interface
//
export interface Session {
  userId: string;
  noPassword?: boolean;
  email: string | undefined;
  impersonating?: boolean;
  tenancy?: Tenancy;
  isRescuePartner?: boolean;
  isVerizonCustomer?: boolean;
  /** Backend language preference (e.g. "EN_US", "FR_CA") — null when unset */
  language?: string | null;
  /** Backend region preference (e.g. "US", "CA") — null when unset */
  region?: string | null;
}

//
// Checkout Interfaces
//
export enum CheckoutStep {
  AddShippingAddress,
  AddPaymentDetails,
  ConfirmPurchase,
  PurchaseComplete,
}

export interface CustomerFullName {
  firstName: string;
  lastName: string | null;
}

export interface Address {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone?: string | null;
}

export interface ShippingInfo {
  customerName: CustomerFullName;
  address: Address;
}

// Address validation
export enum AddressValidationStatus {
  Verified = 'verified',
  NeedsVerifyWithPrompt = 'needs_verify_with_prompt',
  NeedsVerifyWithAutocorrect = 'needs_verify_with_autocorrect',
}

export type AddressValidationNeedsVerify =
  | AddressValidationStatus.NeedsVerifyWithAutocorrect
  | AddressValidationStatus.NeedsVerifyWithPrompt;

export interface BillingToken {
  type: 'paypal';
  id: string;
}

export enum PaymentMethod {
  CreditCard,
  PayPal,
  ApplePay,
  Default = CreditCard,
}

// Components/flows that use PurchaseSubscriptionContextProvider
export enum PaymentContext {
  PURCHASE_SUBSCRIPTION = 'Purchase Subscription',
  MEMBERSHIP_UPGRADE = 'Membership Upgrade',
  MONTHLY_UPGRADE = 'Monthly Upgrade',
  MANAGE_SUBSCRIPTION = 'Manage Subscription',
  RENEW_AND_SAVE = 'Renew and Save',
}

export interface CreditCardInfo {
  __typename: 'ObfuscatedCardInfo';
  lastFour: number;
  type: string;
  month: number;
  year: number;
}

export interface PayPalInfo {
  __typename: 'PayPalInfo';
}

export interface BankAccountInfo {
  __typename: 'BankAccountInfo';
  bankName: string;
  last4: string;
  accountType: string;
}

// Digital wallet (e.g. Apple Pay / Google Pay). Our queries only select the
// __typename for this member, so no detail fields are available here.
export interface WalletPaymentInfo {
  __typename: 'WalletPaymentInfo';
}

export type PaymentInfo = CreditCardInfo | PayPalInfo | BankAccountInfo | WalletPaymentInfo;

export interface BillingInfo {
  firstName: string;
  lastName: string;
  address: Address;
  paymentInfo: PaymentInfo;
}

export interface StripeCheckoutRetryParams {
  confirmationToken: string;
  email: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  phone: string;
  zip: string;
  country: string;
}

export interface CheckoutState {
  addressValidationStatus?: AddressValidationStatus;
  billingInfo?: BillingInfo;
  billingToken?: BillingToken;
  customerName?: CustomerFullName;
  existingUserLoginRequired?: boolean;
  existingUserEmail?: string;
  existingUserHasActiveMembership?: boolean;
  giftRecipient?: GiftRecipientInput;
  orderId?: string;
  shippingAddress?: Address;
  shippingCode?: ShippingCode;
  stripeCheckoutRetryParams?: StripeCheckoutRetryParams;
  unlinkBillingInfo?: boolean;
}

/**
 * Cart + checkout states for one of our few "shops" on the e-commerce
 * site. The main store shop, the upgrade shop, and the subscription
 * shop.
 */
export interface BaseShopState {
  cart: Cart;
  checkout: CheckoutState;
}

export interface StoreShopState extends BaseShopState {
  cart: StoreCart;
}

export interface Series3UpgradePetContext {
  /**
   * We are migrating the upgrade flow that is able to upgrade multiple pets at once to only upgrade one pet at a time.
   * For the legacy flow, there won't be a petId in the context.
   */
  petId?: string;
  preSelectedSubscriptionSku?: string;

  // True if upgrade is taking place inside of in-app cancellation.
  inAppCancellationFlow?: boolean;
  // Since the upgrade flow uses the same components as a first-time purchase
  // we'll use this flag to keep track of whether or not we're in a webview.
  isWebView?: boolean;
}

export interface Series3UpgradeShopState extends BaseShopState {
  cart: Series3UpgradeCart;
  upgradePetContext: Series3UpgradePetContext;
}

export interface SubscriptionShopState extends BaseShopState {
  cart: SubscriptionCart;
}

export interface VerizonShopState extends BaseShopState {
  cart: StoreCart;
}

//
// App Redux state
//
export interface AppState {
  config: ECommerceConfig;
  session: Session | null;
  tenancy: Tenancy | null;

  // For shopping on the main store
  storeShop: StoreShopState;

  // For shopping on the Verizon store
  verizonShop: VerizonShopState;

  // For shopping in-app for a Series 3 upgrade
  series3UpgradeShop: Series3UpgradeShopState;

  // For purchasing a subscription for a specific module
  subscriptionShop: SubscriptionShopState;
}

//
// Promo code types
//
export interface ReferralDetails {
  referralCode: string;
  referrerName?: string;
  referrerAvatarUrl?: string;
  referrerPetAvatarUrl?: string;
}

export interface CouponAmountDiscount {
  name: string;
  type: 'dollars';
  discountAmountInCents: number;
  skus?: string[];
}

export interface CouponPercentDiscount {
  name: string;
  type: 'percent';
  percent: number;
}

export interface CouponMonthDiscount {
  name: string;
  type: 'months';
  months: number;
}

export interface CouponFreeTrialDiscount {
  name: string;
  type: 'free_trial';
  trialUnit: 'month' | 'day' | 'year';
  trialLength: number;
  displayedValueInCents?: number;
}

export type CouponDiscount =
  | CouponAmountDiscount
  | CouponPercentDiscount
  | CouponMonthDiscount
  | CouponFreeTrialDiscount;

export interface CouponDetails {
  code: string;
  /**
   * Absent when the code was validated without pricing, which is what happens on an empty cart:
   * every discount shape depends on the cart contents, so there is nothing honest to report yet.
   */
  discount?: CouponDiscount;
}

export interface GiftCardDetails {
  redemptionCode: string;
  totalValueOfGiftCardInCents: number;
}

export interface IGiftCardDelivery {
  deliverAt: string | null;
  emailAddress: string;
  firstName: string | null;
  lastName: string | null;
  gifterName: string | null;
  personalMessage: string | null;
}

export interface IImage {
  type: 'image';
  url: string;
  descriptors?: string[];
  retinaWidth?: number;
  retinaHeight?: number;
}

export interface IVideo {
  type: 'video';
  url: string;
  thumbnail: IImage;
  /** First frame, so a slide does not flash black before playback starts. */
  poster?: string;
  /** Alternate encodings, offered in order ahead of `url`; lets webm win where it is supported. */
  sources?: { src: string; type: string }[];
  /** Defaults to eager. Pass 'none' for a video far enough down the page to not be worth the bytes. */
  preload?: 'none' | 'metadata' | 'auto';
  /** Lays a scrim and play button over the muted loop; tapping it hands over real controls. */
  showPlayScrim?: boolean;
}

export function createImage(data: Omit<IImage, 'type'>): IImage {
  return {
    type: 'image',
    ...data,
  };
}

export function createVideo(data: Omit<IVideo, 'type'>): IVideo {
  return {
    type: 'video',
    ...data,
  };
}

export type SupplementCheckoutFlow = 'ecomm' | 'in-app';

export type RedemptionType = 'gwp_standard' | 'gwp_free_trial' | null;

export enum FiCurrency {
  /** United States Dollars */
  USD = 'USD',
  /** Canadian Dollars */
  CAD = 'CAD',
  /** British Pounds */
  GBP = 'GBP',
  /** Euros */
  EUR = 'EUR',
  /** Romanian Leu */
  RON = 'RON',
  /** Swedish Krona */
  SEK = 'SEK',
  /** Danish Krone */
  DKK = 'DKK',
  /** Norwegian Krone */
  NOK = 'NOK',
  /** Swiss Franc */
  CHF = 'CHF',
  /** Icelandic Krona */
  ISK = 'ISK',
  /** Turkish Lira */
  TRY = 'TRY',
}
